import {
  SystemPlanningConfig,
  SystemRecommendations,
  EV_CHARGING_RATES,
  HOME_BASE_LOADS,
  PANEL_WATT_RATING,
  SOLAR_COST_PER_WATT,
  BATTERY_COST_PER_KWH,
  GRID_PRICE_AVG_ANNUAL,
  CO2_LBS_PER_KWH,
  SOLAR_PEAK_HOURS_PER_DAY,
  TARGET_RENEWABLE_COVERAGE,
  WIND_BASELINE_KW,
  WIND_CAPACITY_FACTOR,
} from '../types/systemPlanning';

export function getHomeBaseLoadKw(config: SystemPlanningConfig): number {
  if (config.homeSize === 'custom') return Math.max(0, config.customHomeLoadKw);
  return HOME_BASE_LOADS[config.homeSize];
}

export function getTotalEVLoadKw(config: SystemPlanningConfig): number {
  return config.evConfigs
    .slice(0, config.evCount)
    .reduce((sum, ev) => sum + EV_CHARGING_RATES[ev.type], 0);
}

export function getTotalLoadKw(config: SystemPlanningConfig): number {
  return getHomeBaseLoadKw(config) + getTotalEVLoadKw(config);
}

function roundUpToHalf(value: number): number {
  return Math.ceil(value * 2) / 2;
}

function roundUpToFive(value: number): number {
  return Math.ceil(value / 5) * 5;
}

export function calculateRecommendations(config: SystemPlanningConfig): SystemRecommendations {
  const totalLoadKw = getTotalLoadKw(config);

  if (totalLoadKw === 0) {
    return {
      totalLoadKw: 0,
      currentRenewableCoveragePercent: 100,
      recommendedSolarKw: config.currentSolarKw,
      additionalPanelsNeeded: 0,
      recommendedBatteryKwh: config.batteryStorageKwh,
      additionalBatteryKwh: 0,
      estimatedAnnualSavings: 0,
      estimatedCo2ReductionLbs: 0,
      simplePaybackYears: null,
      isAlreadyOptimal: true,
    };
  }

  const annualLoadKwh = totalLoadKw * 8760;
  const currentSolarAnnualKwh = config.currentSolarKw * SOLAR_PEAK_HOURS_PER_DAY * 365;
  const windAnnualKwh = WIND_BASELINE_KW * 8760 * WIND_CAPACITY_FACTOR;
  const currentRenewableTotal = currentSolarAnnualKwh + windAnnualKwh;
  const currentRenewableCoveragePercent = Math.min(100, (currentRenewableTotal / annualLoadKwh) * 100);
  const isAlreadyOptimal = currentRenewableCoveragePercent >= TARGET_RENEWABLE_COVERAGE * 100;

  const renewableTargetKwh = annualLoadKwh * TARGET_RENEWABLE_COVERAGE;
  const renewableDeficitKwh = Math.max(0, renewableTargetKwh - currentRenewableTotal);
  const additionalSolarKwNeeded = renewableDeficitKwh / (SOLAR_PEAK_HOURS_PER_DAY * 365);
  const recommendedSolarKw = roundUpToHalf(config.currentSolarKw + additionalSolarKwNeeded);
  const additionalPanelsNeeded = Math.ceil((additionalSolarKwNeeded * 1000) / PANEL_WATT_RATING);

  const targetBatteryKwh = roundUpToFive(Math.max(10, totalLoadKw * 4));
  const additionalBatteryKwh = Math.max(0, targetBatteryKwh - config.batteryStorageKwh);
  const recommendedBatteryKwh = config.batteryStorageKwh + additionalBatteryKwh;

  const additionalSolarAnnualKwh = additionalSolarKwNeeded * SOLAR_PEAK_HOURS_PER_DAY * 365;
  const estimatedAnnualSavings = additionalSolarAnnualKwh * GRID_PRICE_AVG_ANNUAL;
  const estimatedCo2ReductionLbs = additionalSolarAnnualKwh * CO2_LBS_PER_KWH;

  const installationCost =
    additionalSolarKwNeeded * 1000 * SOLAR_COST_PER_WATT +
    additionalBatteryKwh * BATTERY_COST_PER_KWH;

  const simplePaybackYears =
    isAlreadyOptimal || estimatedAnnualSavings === 0
      ? null
      : installationCost / estimatedAnnualSavings;

  return {
    totalLoadKw,
    currentRenewableCoveragePercent,
    recommendedSolarKw,
    additionalPanelsNeeded,
    recommendedBatteryKwh,
    additionalBatteryKwh,
    estimatedAnnualSavings,
    estimatedCo2ReductionLbs,
    simplePaybackYears,
    isAlreadyOptimal,
  };
}
